import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { authApi, type AdminUser } from "@/modules/auth/api/authApi";
import { SessionContext } from "@/modules/auth/context/sessionContext";

const sessionKey = ["auth", "me"] as const;

export function AdminSessionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const protectedQueries = { predicate: ({ queryKey }: { queryKey: readonly unknown[] }) => queryKey.length !== 2 || queryKey[0] !== "auth" || queryKey[1] !== "me" };
  const session = useQuery({
    queryKey: sessionKey,
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<{ user: AdminUser } | null>(sessionKey);
      const result = await authApi.me(signal);
      signal.throwIfAborted();
      // Explicit login/logout wins over an older current-user read.
      const current = queryClient.getQueryData<{ user: AdminUser } | null>(sessionKey);
      if (current !== previous) return current ?? null;
      if (previous?.user.id !== result?.user.id || previous?.user.role !== result?.user.role) {
        await queryClient.cancelQueries(protectedQueries);
        signal.throwIfAborted();
        queryClient.removeQueries(protectedQueries);
        queryClient.getMutationCache().clear();
      }
      return result;
    },
    retry: false,
  });

  const clearSession = useCallback(async () => {
    await queryClient.cancelQueries();
    // Preserve this observed query so guards receive the signed-out state.
    queryClient.setQueryData(sessionKey, null);
    queryClient.removeQueries({ predicate: ({ queryKey }) => queryKey.length !== 2 || queryKey[0] !== "auth" || queryKey[1] !== "me" });
    queryClient.getMutationCache().clear();
  }, [queryClient]);

  useEffect(() => {
    const clear = () => { void clearSession(); };
    window.addEventListener("tawzeef-admin:unauthorized", clear);
    return () => window.removeEventListener("tawzeef-admin:unauthorized", clear);
  }, [clearSession]);

  const login = useCallback(
    async (email: string, password: string, remember: boolean) => {
      const result = await authApi.login(email, password, remember);
      queryClient.setQueryData(sessionKey, result);
      return result.user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    await clearSession();
  }, [clearSession]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: sessionKey, exact: true }, { cancelRefetch: false });
  }, [queryClient]);

  useEffect(() => {
    const recheck = () => { void refresh(); };
    const visible = () => { if (document.visibilityState === "visible") recheck(); };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("tawzeef-admin:session-check", recheck);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("tawzeef-admin:session-check", recheck);
    };
  }, [refresh]);

  const user: AdminUser | null = session.data?.user ?? null;
  const value = useMemo(
    () => ({
      user,
      loading: session.isPending,
      error: session.error,
      login,
      logout,
      refresh,
      can: (permission: string) => Boolean(user?.effective_permissions.includes(permission)),
    }),
    [login, logout, refresh, session.error, session.isPending, user],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
